import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { TuneOutlined } from '@mui/icons-material';
import LegalLayout, { Bullets, P, SubHeading } from './LegalLayout';
import Seo from './Seo';
import { openConsentPreferences } from '../utils/consent';

// Fixed revision date. This must be updated by hand whenever the policy text
// changes; deriving it from the current date would misrepresent the document.
const LAST_UPDATED = '2026-07-17';

const sections = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    content: (
      <>
        <P>
          Software Systems International (Pvt) Ltd ("SSIPL", "we", "us" or "our") is the data controller responsible for
          the personal data described in this policy. We are a software company registered in Sri Lanka and based in
          Colombo.
        </P>
        <P>
          This policy explains what personal data we collect, why we collect it, the legal basis on which we rely, how
          long we keep it, and the rights available to you. It applies to this website and to the enquiries and support
          requests we handle through it.
        </P>
        <P>
          For questions about this policy or to exercise any of your rights, contact us at info@ssipl.lk. We respond to
          privacy requests within 30 days.
        </P>
      </>
    )
  },
  {
    id: 'data-we-collect',
    title: 'Personal data we collect',
    content: (
      <>
        <P>We collect only what we need to answer your enquiry and to run this website. Specifically:</P>
        <SubHeading>Data you give us</SubHeading>
        <Bullets
          items={[
            <>
              <strong>Contact details:</strong> your name, email address and telephone number when you contact us about
              our services.
            </>,
            <>
              <strong>Business information:</strong> your company name, industry and requirements, where you share them
              during an enquiry or consultation.
            </>,
            <>
              <strong>Correspondence:</strong> the content of emails and messages you send us, so that we can respond and
              keep a record of what was agreed.
            </>
          ]}
        />
        <SubHeading>Data collected automatically</SubHeading>
        <Bullets
          items={[
            <>
              <strong>Technical data:</strong> your IP address, browser type, device type and operating system, which are
              processed by our hosting provider as part of serving and securing the site.
            </>,
            <>
              <strong>Usage data:</strong> pages visited and time spent on them. This is collected only if you consent to
              analytics cookies.
            </>,
            <>
              <strong>Your cookie choice:</strong> stored on your own device so that we do not ask you again on every
              visit.
            </>
          ]}
        />
        <P>
          We do not ask for special category data (such as health, biometric or political data) through this website, and
          we ask that you do not send it to us.
        </P>
      </>
    )
  },
  {
    id: 'legal-basis',
    title: 'Why we use your data and our legal basis',
    content: (
      <>
        <P>
          Under the GDPR we must have a lawful basis for every use of your personal data. Ours are set out below, purpose
          by purpose:
        </P>
        <Bullets
          items={[
            <>
              <strong>Responding to your enquiries and providing support.</strong> Legal basis: steps taken at your
              request before entering a contract, or our legitimate interest in answering people who contact us.
            </>,
            <>
              <strong>Delivering our software and services.</strong> Legal basis: performance of the contract between us.
            </>,
            <>
              <strong>Keeping the site secure and available.</strong> Legal basis: our legitimate interest in preventing
              abuse, fraud and service disruption.
            </>,
            <>
              <strong>Understanding how the site is used so we can improve it.</strong> Legal basis: your consent, given
              through the cookie banner.
            </>,
            <>
              <strong>Sending you updates and marketing.</strong> Legal basis: your consent. You can withdraw it at any
              time and every message includes an unsubscribe link.
            </>,
            <>
              <strong>Meeting our legal, tax and accounting obligations.</strong> Legal basis: compliance with a legal
              obligation.
            </>
          ]}
        />
        <P>
          Where we rely on legitimate interests, we have considered whether those interests are overridden by your rights
          and freedoms, and you may object at any time (see "Your rights" below).
        </P>
      </>
    )
  },
  {
    id: 'cookies',
    title: 'Cookies and similar technologies',
    content: (
      <>
        <P>
          Cookies and browser storage let a site remember information between visits. We group ours into three
          categories:
        </P>
        <Bullets
          items={[
            <>
              <strong>Strictly necessary.</strong> Required for the site to work, including remembering the cookie choice
              you made. These do not require consent and cannot be switched off.
            </>,
            <>
              <strong>Analytics.</strong> Help us see which pages are useful so we can improve them. Set only with your
              consent.
            </>,
            <>
              <strong>Marketing.</strong> Used to measure our campaigns and show you relevant SSIPL content elsewhere.
              Set only with your consent.
            </>
          ]}
        />
        <P>
          No analytics or marketing cookies are placed before you accept them. Rejecting them is as easy as accepting
          them, and your choice never limits your access to this site. You can change or withdraw your choice at any
          time:
        </P>
        <Box sx={{ mt: 2.5 }}>
          <Button variant="outlined" startIcon={<TuneOutlined />} onClick={openConsentPreferences} sx={{ fontWeight: 700 }}>
            Manage cookie preferences
          </Button>
        </Box>
        <P>
          You can also block or delete cookies through your browser settings. Blocking strictly necessary cookies may
          stop parts of the site from working.
        </P>
      </>
    )
  },
  {
    id: 'sharing',
    title: 'Who we share your data with',
    content: (
      <>
        <P>
          We do not sell, rent or trade your personal data. We share it only where necessary, and only with recipients
          bound to protect it:
        </P>
        <Bullets
          items={[
            <>
              <strong>Service providers</strong> who process data on our behalf under a written agreement, such as
              hosting, email and analytics providers. They may act only on our instructions.
            </>,
            <>
              <strong>Professional advisers</strong> such as accountants and lawyers, where needed to protect or enforce
              our rights.
            </>,
            <>
              <strong>Authorities</strong> where we are required to disclose data by law or valid legal process.
            </>,
            <>
              <strong>A successor entity</strong> if our business is reorganised, merged or sold, in which case this
              policy continues to apply to your data.
            </>
          ]}
        />
      </>
    )
  },
  {
    id: 'transfers',
    title: 'International transfers',
    content: (
      <>
        <P>
          We are based in Sri Lanka, which is outside the European Economic Area and has not received an adequacy
          decision from the European Commission. Some of our service providers also operate outside the EEA.
        </P>
        <P>
          Where personal data covered by the GDPR is transferred outside the EEA, we rely on appropriate safeguards under
          Article 46, in practice the European Commission's Standard Contractual Clauses together with supplementary
          technical measures such as encryption in transit and at rest. You may request a copy of the safeguards we use
          by writing to info@ssipl.lk.
        </P>
      </>
    )
  },
  {
    id: 'retention',
    title: 'How long we keep your data',
    content: (
      <>
        <P>
          We keep personal data only for as long as it serves the purpose it was collected for, then delete it or
          anonymise it. In practice:
        </P>
        <Bullets
          items={[
            <>
              <strong>Enquiries that do not become projects:</strong> up to 24 months from our last contact, so we can
              pick up the conversation if you return.
            </>,
            <>
              <strong>Client records:</strong> for the life of the contract and then up to 7 years, to meet Sri Lankan
              tax and accounting requirements.
            </>,
            <>
              <strong>Marketing contacts:</strong> until you withdraw consent or unsubscribe.
            </>,
            <>
              <strong>Analytics data:</strong> up to 26 months in aggregated form.
            </>,
            <>
              <strong>Your cookie choice:</strong> up to 12 months, after which we ask again.
            </>
          ]}
        />
      </>
    )
  },
  {
    id: 'security',
    title: 'How we protect your data',
    content: (
      <P>
        We apply technical and organisational measures appropriate to the risk, including encryption of data in transit,
        access controls on a need-to-know basis, and regular review of our systems. No method of transmission or storage
        is completely secure, so we cannot guarantee absolute security. If a breach occurs that is likely to result in a
        high risk to your rights, we will notify you and the relevant supervisory authority without undue delay and, where
        required, within 72 hours.
      </P>
    )
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    content: (
      <>
        <P>Subject to the conditions in the law, you have the right to:</P>
        <Bullets
          items={[
            <>
              <strong>Be informed</strong> about how your data is used, which is the purpose of this policy.
            </>,
            <>
              <strong>Access</strong> your personal data and receive a copy of it.
            </>,
            <>
              <strong>Rectification</strong> of data that is inaccurate or incomplete.
            </>,
            <>
              <strong>Erasure</strong> of your data where there is no continuing reason for us to hold it.
            </>,
            <>
              <strong>Restrict processing</strong> while an issue you have raised is investigated.
            </>,
            <>
              <strong>Data portability:</strong> receive the data you gave us in a structured, commonly used,
              machine-readable format, and have it sent to another controller where technically feasible.
            </>,
            <>
              <strong>Object</strong> to processing based on our legitimate interests, and to direct marketing at any
              time.
            </>,
            <>
              <strong>Withdraw consent</strong> at any time, without affecting the lawfulness of processing carried out
              before withdrawal.
            </>,
            <>
              <strong>Not be subject to automated decision-making</strong> that produces legal or similarly significant
              effects. We do not carry out such decision-making.
            </>
          ]}
        />
        <P>
          To exercise any of these rights, email info@ssipl.lk. We will respond within 30 days and will not charge a fee
          unless a request is manifestly unfounded or excessive. We may ask you to verify your identity first.
        </P>
        <P>
          If you are in the EEA or the UK and believe we have not handled your data properly, you may lodge a complaint
          with your local data protection supervisory authority. We would appreciate the chance to address your concern
          first.
        </P>
      </>
    )
  },
  {
    id: 'children',
    title: "Children's privacy",
    content: (
      <P>
        This website and our services are intended for businesses and are not directed at children under 16. We do not
        knowingly collect personal data from children. If you believe a child has provided us with personal data, contact
        us at info@ssipl.lk and we will delete it.
      </P>
    )
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    content: (
      <P>
        We may update this policy to reflect changes in our practices or in the law. The revision date at the top of this
        page always shows when it last changed. If a change materially affects how we use your data, we will make it
        clear on this website and, where required, ask for your consent again.
      </P>
    )
  },
  {
    id: 'contact',
    title: 'Contact us',
    content: (
      <>
        <P>
          For any question about this policy, to exercise your rights, or to raise a concern about how we handle personal
          data, contact us using the details below. We aim to reply within two business days.
        </P>
      </>
    )
  }
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy',
  url: 'https://ssipl.lk/privacy',
  description: 'How Software Systems International (Pvt) Ltd collects, uses and protects personal data.',
  dateModified: LAST_UPDATED,
  isPartOf: { '@id': 'https://ssipl.lk/#website' }
};

const PrivacyPage = () => (
  <>
    <Seo
      title="Privacy Policy | SSIPL"
      description="How Software Systems International (Pvt) Ltd collects, uses, shares and protects your personal data, including your GDPR rights, cookie choices and data retention periods."
      path="/privacy"
      jsonLd={jsonLd}
    />
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      intro={
        <Typography component="span" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          Your privacy matters to us. This policy sets out what we collect, why we collect it, how long we keep it and the
          control you have over it, in plain language.
        </Typography>
      }
      lastUpdated={LAST_UPDATED}
      sections={sections}
    />
  </>
);

export default PrivacyPage;
